import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './App.css';
import {
  MapPin, MessageCircle, ArrowDown, Star, Cake,
  Shirt, Volume2, VolumeX, Calendar, Check, X
} from 'lucide-react';
import { listaInvitados } from './data/invitados';
// 🐻 Personajes: reemplaza estos PNG por los reales (mismo nombre) en src/image/
import masha from './image/masha.png';
import oso from './image/oso.png';
import fotoAcha from './image/acha.jpeg';
import fotoJuntos from './image/hacha_oso_masha.jpg';
import fotoInicio from './image/foto_inicio.png';
// 🎵 Canción de fondo de Masha y el Oso (reemplaza el archivo masha.mp3 en src/music/)
import musicaFondo from './music/masha.mp3';

/* ===========================================================
   Datos de la fiesta — Cumpleaños de Acha 🐻
   Tema: Masha y el Oso · 2 añitos
   =========================================================== */
const partyData = {
  name: "Arissa Hadasa",
  nickname: "Arissa Hadasa",
  age: 2,
  // Horario de la fiesta (hora local). El conteo regresivo usa la hora de inicio.
  startDate: "2026-10-31T14:30:00",
  endDate: "2026-10-31T19:00:00",
  timePending: false,
  locationPending: false,
  locationName: "Salón de fiestas Diversitopia",
  locationAddress: "Oaxaca, Oax.",
  locationUrl: "https://www.google.com/maps?ll=17.117898,-96.769162&z=16&t=m&hl=es&gl=MX&mapclient=embed&cid=3592587664073306141",
  locationCoords: "17.117898,-96.769162", // para el mapa incrustado y "cómo llegar"
  // 👇 Cambia estos números y nombres por los reales (formato: 52 + 10 dígitos)
  hosts: { mom: "Mamá", dad: "Papá" },
  phones: { mom: "529515324897", dad: "529511233759" },
  dressCode: "Colores alegres 🎨",
};

// Carpeta donde viven guardar.php / leer.php (mismo dominio en producción).
const RSVP_API = process.env.REACT_APP_RSVP_API || '/api';

const MESES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

const formatDayBadge = (dateStr) => {
  const d = new Date(dateStr);
  return { day: d.getDate(), monthYear: `${MESES[d.getMonth()]} ${d.getFullYear()}` };
};

const formatTime = (dateStr) => {
  const d = new Date(dateStr);
  let h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
};

// "2026-10-31T14:00:00" -> "20261031T140000" (formato de Google Calendar)
const toCalendarStamp = (dateStr) => dateStr.replace(/[-:]/g, '');

/* Divisor con florecitas y osito 🌸 */
const DuckDivider = ({ light = false }) => (
  <div className={`duck-divider ${light ? 'duck-divider--light' : ''}`}>
    <span className="dd-line" />
    <span className="dd-bubble" />
    <span className="dd-duck">🐻</span>
    <span className="dd-bubble" />
    <span className="dd-line" />
  </div>
);

const Countdown = ({ targetDate }) => {
  const calculateTimeLeft = () => {
    const difference = +new Date(targetDate) - +new Date();
    if (difference <= 0) return { dias: 0, horas: 0, min: 0, seg: 0 };
    return {
      dias: Math.floor(difference / (1000 * 60 * 60 * 24)),
      horas: Math.floor((difference / (1000 * 60 * 60)) % 24),
      min: Math.floor((difference / 1000 / 60) % 60),
      seg: Math.floor((difference / 1000) % 60),
    };
  };
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  useEffect(() => {
    const timer = setTimeout(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearTimeout(timer);
  });
  const timerComponents = Object.keys(timeLeft).map((interval) => {
    if (!timeLeft[interval] && timeLeft[interval] !== 0) return null;
    return (
      <div className="time-item" key={interval}>
        <span className="time-val">{timeLeft[interval]}</span>
        <span className="time-label">{interval}</span>
      </div>
    );
  });
  return (
    <div className="countdown-wrapper">
      {timerComponents.length ? timerComponents : <span className="countdown-today">¡Es hoy! 🎉</span>}
    </div>
  );
};

function Invitation() {
  const { id } = useParams();
  const [invitado, setInvitado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [respuesta, setRespuesta] = useState(null); // 'si' | 'no' | null
  const audioRef = useRef(null);
  const contentRef = useRef(null);

  // --- NUEVAS MEJORAS DE DISEÑO ---
  // Burbujas en el Hero
  const [bubbles, setBubbles] = useState([]);
  
  // Scroll Parallax
  const [scrollY, setScrollY] = useState(0);

  // Confeti y Estela de Partículas
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animFrameRef = useRef(null);

  // Spawn de burbujas en el Hero
  useEffect(() => {
    if (loading) return;
    
    const spawnInterval = setInterval(() => {
      setBubbles((prev) => {
        if (prev.length >= 10) return prev;
        return [
          ...prev,
          {
            id: Date.now() + Math.random(),
            left: Math.random() * 85 + 5, // % left
            size: Math.random() * 22 + 16, // px size (16px a 38px)
            duration: Math.random() * 5 + 6, // 6s a 11s para flotar
            delay: Math.random() * 1.5
          }
        ];
      });
    }, 1400);

    return () => clearInterval(spawnInterval);
  }, [loading]);

  // Listener para Parallax de Scroll
  useEffect(() => {
    if (loading) return;
    const handleScroll = () => {
      const offset = contentRef.current ? contentRef.current.scrollTop : window.scrollY;
      setScrollY(offset);
    };
    const container = contentRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    window.addEventListener('scroll', handleScroll);
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, [loading]);

  const popBubble = (bubbleId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setBubbles((prev) =>
      prev.map((b) => (b.id === bubbleId ? { ...b, popping: true } : b))
    );
    setTimeout(() => {
      setBubbles((prev) => prev.filter((b) => b.id !== bubbleId));
    }, 250);
  };

  // Motor unificado de partículas (Confeti + Trail)
  const ensureAnimationLoopRunning = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    if (animFrameRef.current) return;
    
    const update = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const list = particlesRef.current;
      if (list.length === 0) {
        animFrameRef.current = null;
        return;
      }
      particlesRef.current = list.filter((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += p.gravity;
        p.rotation += p.rSpeed;
        p.opacity -= p.fadeRate || 0.008;
        
        if (p.opacity <= 0 || p.y > canvas.height + 50) return false;
        
        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        
        if (p.emoji) {
          ctx.font = `${p.size * 2.2}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(p.emoji, 0, 0);
        } else {
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        }
        ctx.restore();
        return true;
      });
      animFrameRef.current = requestAnimationFrame(update);
    };
    animFrameRef.current = requestAnimationFrame(update);
  };

  const triggerConfetti = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const colors = ['#C71C6D', '#F7C9DF', '#4A9E5A', '#8B5A2B', '#CDEDFB', '#FFD93D', '#FF6B6B'];
    const symbols = ['🎈', '🐻', '🎉', '✨', '🌸'];
    
    const newParticles = [];
    const count = 120;
    
    for (let i = 0; i < count; i++) {
      const side = Math.random() > 0.5 ? 'left' : 'right';
      newParticles.push({
        x: side === 'left' ? 0 : canvas.width,
        y: canvas.height * 0.8 + Math.random() * (canvas.height * 0.2),
        vx: (side === 'left' ? 1 : -1) * (Math.random() * 8 + 5),
        vy: - (Math.random() * 16 + 9),
        size: Math.random() * 9 + 6,
        color: colors[Math.floor(Math.random() * colors.length)],
        emoji: Math.random() < 0.32 ? symbols[Math.floor(Math.random() * symbols.length)] : null,
        rotation: Math.random() * 360,
        rSpeed: Math.random() * 4 - 2,
        gravity: 0.4,
        opacity: 1,
        fadeRate: 0.008
      });
    }
    
    particlesRef.current = [...particlesRef.current, ...newParticles];
    ensureAnimationLoopRunning();
  };

  // Cursor Trail
  const handleMouseMoveTrail = (e) => {
    if (Math.random() > 0.15) return; // Limitar frecuencia de generación
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Asegurar tamaño correcto de canvas por si hubo redimensionamiento
    if (canvas.width !== window.innerWidth || canvas.height !== window.innerHeight) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const colors = ['rgba(249, 228, 110, 0.7)', 'rgba(255, 165, 0, 0.7)', 'rgba(205, 237, 251, 0.8)', 'rgba(166, 221, 247, 0.8)'];
    const symbols = ['🫧', '✨'];
    
    const newParticle = {
      x,
      y,
      vx: (Math.random() * 2 - 1) * 1.2,
      vy: - (Math.random() * 1.2 + 0.8),
      size: Math.random() * 8 + 5,
      color: colors[Math.floor(Math.random() * colors.length)],
      emoji: Math.random() < 0.45 ? symbols[Math.floor(Math.random() * symbols.length)] : null,
      rotation: Math.random() * 360,
      rSpeed: Math.random() * 2 - 1,
      gravity: 0.03,
      opacity: 0.85,
      fadeRate: 0.016 // se desvanece rápido
    };
    
    particlesRef.current.push(newParticle);
    ensureAnimationLoopRunning();
  };

  const handleTouchMoveTrail = (e) => {
    if (e.touches.length === 0) return;
    if (Math.random() > 0.15) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    if (canvas.width !== window.innerWidth || canvas.height !== window.innerHeight) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    
    const colors = ['rgba(249, 228, 110, 0.7)', 'rgba(255, 165, 0, 0.7)', 'rgba(205, 237, 251, 0.8)', 'rgba(166, 221, 247, 0.8)'];
    const symbols = ['🫧', '✨'];
    
    const newParticle = {
      x,
      y,
      vx: (Math.random() * 2 - 1) * 1.2,
      vy: - (Math.random() * 1.2 + 0.8),
      size: Math.random() * 8 + 5,
      color: colors[Math.floor(Math.random() * colors.length)],
      emoji: Math.random() < 0.45 ? symbols[Math.floor(Math.random() * symbols.length)] : null,
      rotation: Math.random() * 360,
      rSpeed: Math.random() * 2 - 1,
      gravity: 0.03,
      opacity: 0.85,
      fadeRate: 0.016
    };
    
    particlesRef.current.push(newParticle);
    ensureAnimationLoopRunning();
  };

  // Manejador 3D Tilt interactivo para tarjetas
  const handleCardMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const xc = rect.width / 2;
    const yc = rect.height / 2;
    const tiltX = ((yc - y) / yc) * 12; // Inclinación máx. 12 grados
    const tiltY = ((x - xc) / xc) * -12; // Inclinación máx. 12 grados
    
    card.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) translateY(-6px) scale(1.03)`;
  };

  const handleCardMouseLeave = (e) => {
    const card = e.currentTarget;
    card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px) scale(1)`;
  };

  // El invitado elige Sí/No: se registra en el sistema al instante.
  // Solo funciona si el enlace es personalizado (hay invitado en la lista).
  const responderSi = () => {
    if (!invitado) return;
    setRespuesta('si');
    triggerConfetti();
    registrarRSVP('si');
  };
  const responderNo = () => {
    if (!invitado) return;
    setRespuesta('no');
    registrarRSVP('no');
  };
  // Botón opcional de WhatsApp: manda el mensaje personal y deja registro del contacto.
  const enviarWhatsApp = (contacto) => {
    registrarRSVP(respuesta || 'si', contacto);
  };
  // Mensaje personal de WhatsApp según la respuesta.
  const textoWhats = (asistencia) => {
    const quien = invitado ? invitado.nombre : '';
    if (asistencia === 'no') {
      return `¡Hola! 👋 Soy ${quien}.\n\n` +
        `Muchas gracias por invitarnos al cumple de ${partyData.nickname} 🐻.\n` +
        `Lamentamos mucho no poder acompañarlos esta vez. 💜\n\n` +
        `¡Les deseamos una fiesta preciosa!`;
    }
    return `¡Hola! 👋 Soy ${quien}.\n\n` +
      `¡Muchas gracias por invitarnos al cumple de ${partyData.nickname}! 🐻🎈\n` +
      `✅ Confirmo con gusto nuestra asistencia.\n\n` +
      `¡Ahí nos vemos para festejar! 💜`;
  };

  useEffect(() => {
    audioRef.current = new Audio(musicaFondo);
    audioRef.current.loop = true;
    audioRef.current.volume = 0.5;
    const playPromise = audioRef.current.play();
    if (playPromise !== undefined) {
      playPromise.then(() => {
        setIsPlaying(true);
      }).catch(() => {
        setIsPlaying(false);
      });
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, []);

  const toggleMusic = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const [autoScroll, setAutoScroll] = useState(true);
  useEffect(() => {
    if (loading || !autoScroll) return;
    const el = contentRef.current;
    const scrollInterval = setInterval(() => {
      if (el) {
        el.scrollBy(0, 1);
        if (el.scrollTop + el.clientHeight >= el.scrollHeight) setAutoScroll(false);
      } else {
        window.scrollBy(0, 1);
        if (window.innerHeight + window.scrollY >= document.body.offsetHeight) setAutoScroll(false);
      }
    }, 40);
    return () => clearInterval(scrollInterval);
  }, [loading, autoScroll]);

  useEffect(() => {
    const stopScrolling = () => {
      if (autoScroll) setAutoScroll(false);
    };
    window.addEventListener("wheel", stopScrolling);
    window.addEventListener("touchmove", stopScrolling);
    window.addEventListener("keydown", stopScrolling);
    window.addEventListener("mousedown", stopScrolling);
    return () => {
      window.removeEventListener("wheel", stopScrolling);
      window.removeEventListener("touchmove", stopScrolling);
      window.removeEventListener("keydown", stopScrolling);
      window.removeEventListener("mousedown", stopScrolling);
    };
  }, [autoScroll]);

  // Revela cada sección con un fundido suave al entrar en pantalla.
  useEffect(() => {
    if (loading) return;
    const els = document.querySelectorAll('.reveal');
    if (!els.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [loading]);

  const addToCalendar = () => {
    const title = encodeURIComponent(`Cumpleaños de ${partyData.nickname} 🐻 (${partyData.age} años)`);
    const details = encodeURIComponent(
      `¡Masha y el Oso están de fiesta porque ${partyData.nickname} cumple ${partyData.age} añitos! 🎈`
    );
    const location = encodeURIComponent(partyData.locationPending ? "Por confirmar" : `${partyData.locationName}, ${partyData.locationAddress}`);
    const start = toCalendarStamp(partyData.startDate);
    const end = toCalendarStamp(partyData.endDate);
    const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}&ctz=America/Mexico_City`;
    window.open(url, '_blank');
  };

  // Registra la respuesta en el servidor (guardar.php) sin bloquear nada.
  // asistencia: 'si' | 'no' · contacto: 'mama' | 'papa' | '' (opcional)
  const registrarRSVP = (asistencia, contacto = '') => {
    try {
      fetch(`${RSVP_API}/guardar.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          id: id || '',
          nombre: invitado ? invitado.nombre : '',
          pases: invitado ? invitado.pases : '',
          host: invitado ? invitado.host : '',
          asistencia,
          contacto,
        }),
      }).catch(() => {});
    } catch (e) { /* sin conexión: ignoramos */ }
  };

  useEffect(() => {
    let cancelado = false;
    if (id) {
      // El servidor manda (refleja ediciones/altas/bajas hechas desde el admin).
      // Si no hay conexión (o no hay PHP en local), usamos la lista base del código.
      const usarBase = () => { if (listaInvitados[id]) setInvitado(listaInvitados[id]); };
      fetch(`${RSVP_API}/invitados.php?id=${encodeURIComponent(id)}`)
        .then((r) => r.json())
        .then((d) => {
          if (cancelado) return;
          if (d.ok && d.invitado) setInvitado(d.invitado); // agregado o base editado
          else if (d.eliminado) setInvitado(null);         // fue dado de baja
          else usarBase();                                 // base sin cambios
        })
        .catch(() => { if (!cancelado) usarBase(); });
    }
    const timer = setTimeout(() => {
      setLoading(false);
    }, 4000);
    return () => { cancelado = true; clearTimeout(timer); };
  }, [id]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-duck">
            <img src={oso} alt="El Oso" className="loading-duck-img" />
            <img src={masha} alt="Masha" className="loading-duck-img loading-duck-img--masha" />
            <span className="loading-bubble b1" />
            <span className="loading-bubble b2" />
            <span className="loading-bubble b3" />
          </div>
          <h2 className="loading-names">{partyData.nickname}</h2>
          <p className="loading-tagline">Preparando la fiesta en el bosque...</p>
          <div className="loading-photo">
            <img src={fotoInicio} alt={partyData.nickname} className="loading-photo-img" />
          </div>
        </div>
      </div>
    );
  }

  const welcomeMessage = invitado?.mensaje ||
    "Será un día lleno de risas, aventuras y travesuras en el bosque. Nos encantará que vengas a celebrar mis dos años con Masha, el Oso y con nosotros.";

  const { day, monthYear } = formatDayBadge(partyData.startDate);

  return (
    <div 
      className="layout"
      onMouseMove={handleMouseMoveTrail}
      onTouchMove={handleTouchMoveTrail}
    >
      <div className="mobile-bg" />

      {/* Escena del patito sticky (desktop) / hero (móvil) */}
      <div className="side-image-container">
        <div className="hero-scene">
          <span 
            className="cloud cloud-1" 
            style={{ transform: `translateX(${scrollY * 0.15}px)` }}
          />
          <span 
            className="cloud cloud-2" 
            style={{ transform: `translateX(${-scrollY * 0.12}px)` }}
          />
          {bubbles.map((b) => (
            <span
              key={b.id}
              className={`hero-bubble ${b.popping ? 'popping' : ''}`}
              style={{
                left: `${b.left}%`,
                width: `${b.size}px`,
                height: `${b.size}px`,
                animationDuration: `${b.duration}s`,
                animationDelay: `${b.delay}s`
              }}
              onClick={(e) => popBubble(b.id, e)}
              onTouchStart={(e) => popBubble(b.id, e)}
            />
          ))}
          <div
            className="hero-duck-wrapper"
            style={{ transform: `translateY(${-scrollY * 0.08}px) rotate(${scrollY * 0.012}deg)` }}
          >
            <img src={oso} alt="El Oso" className="hero-char hero-char--oso" />
            <span className="hero-kid">
              <img src={fotoInicio} alt={partyData.nickname} className="hero-char hero-char--kid" />
            </span>
            <img src={masha} alt="Masha" className="hero-char hero-char--masha" />
          </div>
          <span
            className="water"
            style={{ transform: `translateY(${scrollY * 0.04}px)` }}
          />
        </div>
        <div className="overlay-mobile">
          <p className="mobile-eyebrow">¡Cumplo 2 años!</p>
          <h1 className="mobile-title">{partyData.nickname}</h1>
        </div>
      </div>

      <div className="content-container" ref={contentRef}>

        {/* Header */}
        <section className="section header-section reveal">
          <div className="sparkles-container">
            <Star className="sparkle sparkle-1" size={18} />
            <Star className="sparkle sparkle-2" size={14} />
          </div>
          <p className="overline">¡Estás invitado a mi cumpleaños!</p>
          <h1 className="desktop-title">{partyData.nickname}</h1>
          <div className="age-badge">
            <Cake size={20} /> ¡Cumplo {partyData.age} añitos!
          </div>
          <p className="verse">
            <strong>Hadasa, el oso y masha</strong> están de fiesta 🐻 y quieren celebrar
             este día tan especial.
          </p>
          <DuckDivider />
          <p className="scripture-quote">
            "En el bosque hoy hay fiesta, <br />
            con Hadasa, el oso y Masha a mi lado, <br />
            porque cumplo {partyData.age} añitos <br />
            rodeada de quienes más quiero."
            <span className="scripture-source">— {partyData.name} —</span>
          </p>
          <ArrowDown className="scroll-icon" size={20} />
        </section>

        {/* Foto de inicio */}
        <section className="section intro-photo-section reveal">
          <div className="intro-photo-frame">
            <img src={fotoInicio} alt={partyData.nickname} className="intro-photo-img" />
            <span className="intro-photo-badge">🐻</span>
          </div>
          <p className="intro-photo-caption">¡{partyData.nickname} cumple {partyData.age}! 💜</p>
        </section>

        {/* Fecha */}
        <section className="section date-section reveal">
          <div className="celebration-banner">
            <span className="floating-duck duck-left">🐻</span>
            <div className="date-badge">
              <span className="day-number">{day}</span>
              <span className="month-year">{monthYear}</span>
            </div>
            <span className="floating-duck duck-right">👧</span>
          </div>
          <p className="time-text">
            🕐 {partyData.timePending ? 'Horario por confirmar' : `${formatTime(partyData.startDate)} – ${formatTime(partyData.endDate)}`}
          </p>
          {partyData.timeNote && <p className="time-note">{partyData.timeNote}</p>}
          <Countdown targetDate={partyData.startDate} />
          <button onClick={addToCalendar} className="btn-outline btn-calendar">
            <Calendar size={16} /> Agregar al calendario
          </button>
        </section>

        {/* Divisores de agua ondulados animados (SVG Waves) */}
        <div className="wave-divider-container" aria-hidden="true">
          <svg className="waves" xmlns="http://www.w3.org/2000/svg" viewBox="0 24 150 28" preserveAspectRatio="none" shapeRendering="auto">
            <defs>
              <path id="gentle-wave" d="M-160 44c30 0 58-18 88-18s58 18 88 18 58-18 88-18 58 18 88 18v44h-352z" />
            </defs>
            <g className="parallax-waves">
              <use href="#gentle-wave" x="48" y="0" fill="rgba(120, 200, 130, 0.7)" />
              <use href="#gentle-wave" x="48" y="3" fill="rgba(74, 158, 90, 0.5)" />
              <use href="#gentle-wave" x="48" y="5" fill="rgba(120, 200, 130, 0.3)" />
              <use href="#gentle-wave" x="48" y="7" fill="var(--color-forest)" />
            </g>
          </svg>
          <div className="wave-duck-overlay">
            <span className="wd-duck">🐻</span>
            <span className="wd-duck">🌲</span>
            <span className="wd-duck">👧</span>
            <span className="wd-duck">🎈</span>
            <span className="wd-duck">🌸</span>
          </div>
        </div>

        {/* Saludo personalizado */}
        <section className="section welcome-msg-section reveal">
          <div className="acha-photo-frame">
            <img src={fotoAcha} alt={partyData.name} className="acha-photo" />
            <span className="acha-photo-badge">🐻 {partyData.age}</span>
          </div>
          <p className="acha-photo-caption">¡Feliz cumple, {partyData.nickname}!</p>
          {invitado && (
            <div 
              className="ticket-card"
              onMouseMove={handleCardMouseMove}
              onMouseLeave={handleCardMouseLeave}
            >
              <p className="ticket-label">Invitación especial para</p>
              <h2 className="ticket-name">{invitado.nombre}</h2>
              {Number(invitado.pases) > 0 && (
                <div className="ticket-passes">
                  <span className="ticket-passes-count">
                    🎟️ {invitado.pases} {Number(invitado.pases) === 1 ? 'pase' : 'pases'}
                  </span>
                </div>
              )}
            </div>
          )}
          <p className="welcome-text">"{welcomeMessage}"</p>
        </section>

        {/* Foto de Arissa con Masha y el Oso */}
        <section className="section photo-together-section reveal">
          <div className="photo-together-frame">
            <img src={fotoJuntos} alt="Arissa con Masha y el Oso" className="photo-together-img" />
          </div>
          <p className="photo-together-caption">Arissa, Masha y el Oso 🐻💜</p>
        </section>

        {/* Galería / tarjetas del patito */}
        <section className="section gallery-section reveal">
          <p className="overline">¡A festejar!</p>
          <div className="gallery-scroll-container">
            <div
              className="duck-card duck-card--yellow"
              onMouseMove={handleCardMouseMove}
              onMouseLeave={handleCardMouseLeave}
            >
              <img src={masha} alt="Masha" className="duck-card-img" />
              <span className="duck-card-caption">¡Hola!</span>
            </div>
            <div
              className="duck-card duck-card--sky"
              onMouseMove={handleCardMouseMove}
              onMouseLeave={handleCardMouseLeave}
            >
              <span className="duck-card-emoji">🎂</span>
              <span className="duck-card-caption">{partyData.age} añitos</span>
            </div>
            <div
              className="duck-card duck-card--orange"
              onMouseMove={handleCardMouseMove}
              onMouseLeave={handleCardMouseLeave}
            >
              <img src={oso} alt="El Oso" className="duck-card-img" />
              <span className="duck-card-caption">¡A jugar!</span>
            </div>
          </div>
        </section>

        {/* Ubicación y Dress code */}
        <section className="section location-section reveal">
          <div className="icon-badge">
            <MapPin size={22} />
          </div>
          <h3>¿Dónde será la fiesta?</h3>
          <p className="venue-name">{partyData.locationName}</p>
          <p className="venue-address">{partyData.locationAddress}</p>
          {partyData.locationCoords ? (
            <>
              <div className="map-embed">
                <iframe
                  title="Mapa del salón"
                  src={`https://maps.google.com/maps?q=${partyData.locationCoords}&z=16&hl=es&output=embed`}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
              <div className="map-buttons">
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${partyData.locationCoords}`}
                  target="_blank" rel="noreferrer" className="btn-outline"
                >
                  <MapPin size={16} /> Cómo llegar
                </a>
                <a href={partyData.locationUrl} target="_blank" rel="noreferrer" className="btn-outline btn-outline--soft">
                  Ver en Google Maps
                </a>
              </div>
            </>
          ) : (
            <span className="btn-outline btn-outline--disabled">Muy pronto 🐻</span>
          )}
          <div className="dress-code-container">
            <div className="divider-small" />
            <div className="icon-badge">
              <Shirt size={22} />
            </div>
            <p className="dress-code-label">Código de vestimenta</p>
            <p className="dress-code-value">{partyData.dressCode}</p>
          </div>
        </section>

        {/* RSVP */}
        <section className="section rsvp-section reveal">
          <DuckDivider light />
          <h3>¿Nos acompañas?</h3>
          <div className="rsvp-intro-wrap">
            <p className="rsvp-intro">
              Estamos preparando cada detalle con mucho amor.<br />
              <strong>Cuéntanos si podrás acompañarnos.</strong>
            </p>
            <p className="rsvp-deadline">
              Por favor confirma antes del <strong>24 de Octubre</strong>.
            </p>
          </div>

          {/* Paso 1 — Sí / No (solo para invitados con enlace personalizado) */}
          {invitado ? (
            <div className="rsvp-choice">
              <button
                type="button"
                className={`btn-choice btn-choice--si ${respuesta === 'si' ? 'is-active' : ''}`}
                onClick={responderSi}
              >
                <Check size={20} /> Sí, asistiré
              </button>
              <button
                type="button"
                className={`btn-choice btn-choice--no ${respuesta === 'no' ? 'is-active' : ''}`}
                onClick={responderNo}
              >
                <X size={20} /> No podré ir
              </button>
            </div>
          ) : (
            <p className="rsvp-nolist">
              Para confirmar tu asistencia, abre el <strong>enlace personalizado</strong> que
              te compartimos. 💜
            </p>
          )}

          {/* Paso 2 — Mensaje personal por WhatsApp (aparece tras responder) */}
          {respuesta && (
            <div className="rsvp-followup">
              <p className="rsvp-followup-text">
                {respuesta === 'si'
                  ? '¡Qué alegría! 🎉 Tu asistencia quedó registrada. Si quieres, mándanos también un mensajito:'
                  : 'Gracias por avisarnos 💜 Ya quedó registrado. Si gustas, escríbenos:'}
              </p>
              <div className="rsvp-buttons">
                <a
                  href={`https://wa.me/${partyData.phones.mom}?text=${encodeURIComponent(textoWhats(respuesta))}`}
                  onClick={() => enviarWhatsApp('mama')}
                  className="btn-rsvp" target="_blank" rel="noreferrer"
                >
                  <MessageCircle size={16} /> Mensaje a {partyData.hosts.mom}
                </a>
                <a
                  href={`https://wa.me/${partyData.phones.dad}?text=${encodeURIComponent(textoWhats(respuesta))}`}
                  onClick={() => enviarWhatsApp('papa')}
                  className="btn-rsvp" target="_blank" rel="noreferrer"
                >
                  <MessageCircle size={16} /> Mensaje a {partyData.hosts.dad}
                </a>
              </div>
            </div>
          )}

          <p className="exclusive-note">
            {respuesta === 'no' ? 'Te vamos a extrañar 💜' : '¡Te esperamos con muchos globos! 🎈'}
          </p>
        </section>

        <footer className="footer-simple">
          <span className="footer-duck">🐻</span>
          <small>Con cariño, {partyData.name}.</small>
          <span className="footer-duck">👧</span>
        </footer>
      </div>

      {/* Widget música flotante */}
      <button
        onClick={toggleMusic}
        className={`music-pill-fixed ${isPlaying ? 'is-playing' : ''}`}
        aria-label={isPlaying ? 'Pausar música' : 'Reproducir música'}
      >
        <span className="music-icon-wrap">
          {isPlaying ? <Volume2 size={20} /> : <VolumeX size={20} />}
          {isPlaying && <span className="ripple" />}
        </span>
        <div className="music-equalizer" aria-hidden="true">
          <span className="eq-bar" />
          <span className="eq-bar" />
          <span className="eq-bar" />
          <span className="eq-bar" />
        </div>
      </button>

      {/* Canvas para confeti */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100dvh',
          pointerEvents: 'none',
          zIndex: 9999
        }}
      />
    </div>
  );
}

export default Invitation;
